import { useEffect, useRef } from "react";
import { formatThroughput } from "../format";

/**
 * Progress bar for a running simulation.
 *
 * Two modes:
 * - `total != null` (determinate) → bar at `done/total`. Progress arrives
 *   in discrete chunk-completion steps — one per GPU dispatch chunk
 *   (~3.8M spins each, capped by the Pass-1.5/Pass-2 1D dispatch limit in
 *   `compute_chunk_size`), so a 1B run reports in ~0.4% increments
 *   separated by GPU-bound pauses. Binding the fill width straight to those
 *   steps reads as "stuck" between updates, so we ease the fill toward the
 *   latest target every animation frame — mutating the node's width
 *   directly (not via React state) so the motion runs independently of how
 *   coarsely/often new progress lands.
 * - `total == null` (indeterminate) → animated bar. This happens on the
 *   CPU-WASM path (no fine-grained progress) AND on a WebGPU run before its
 *   first chunk-completion tick lands, so the caption MUST reflect the
 *   actual `engine` rather than assume CPU — otherwise an in-flight WebGPU
 *   run is mislabeled "CPU-WASM running…" (MAD-1982).
 *
 * `spinsPerSec`, when supplied, appends a live throughput readout to the
 * determinate caption (cumulative average = done / elapsed).
 */
export function ProgressBar({
  done,
  total,
  spinsPerSec,
  engine,
}: {
  done: number;
  total: number | null;
  spinsPerSec?: number | null;
  /** Active backend, used only for the indeterminate caption. Defaults to
   * "webgpu" (the local + cloud GPU adapters both report mode "webgpu").
   * `undefined` is accepted explicitly (the caller passes the adapter mode,
   * which is absent until the adapter is ready) and falls back to "webgpu". */
  engine?: "webgpu" | "cpu" | undefined;
}) {
  const barRef = useRef<HTMLDivElement | null>(null);
  const target =
    total != null && total > 0 ? Math.min(100, (done / total) * 100) : 0;
  // Latest target read by the rAF loop without re-subscribing the effect.
  const targetRef = useRef(target);
  targetRef.current = target;

  useEffect(() => {
    if (total == null) return undefined;
    let raf = 0;
    // <0 sentinel: seed `current` from the rendered width on the first
    // frame so the ease resumes where the node last sat (0 on mount, via
    // the `w-0` class) rather than jumping. The rAF loop is the *sole*
    // writer of `el.style.width` — the JSX no longer sets an inline width,
    // which would otherwise snap the DOM back to `target` every render and
    // defeat the smoothing.
    let current = -1;
    const tick = () => {
      const el = barRef.current;
      if (el != null) {
        if (current < 0) current = Number.parseFloat(el.style.width) || 0;
        const diff = targetRef.current - current;
        // Snap when close enough; otherwise close 20% of the gap per
        // frame (~0.5s glide to a far target, smooth creep for near ones).
        current =
          Math.abs(diff) < 0.1 ? targetRef.current : current + diff * 0.2;
        el.style.width = `${current}%`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [total]);

  if (total == null) {
    return (
      <div className="w-full">
        <div className="relative h-1.5 w-full overflow-hidden bg-paper-border dark:bg-term-border">
          <div
            className="absolute inset-y-0 w-1/3 animate-pulse bg-amber-500"
            style={{
              animation: "progress-indeterminate 1.5s ease-in-out infinite",
            }}
          />
        </div>
        <p className="mt-1 text-xs text-paper-text dark:text-term-text">
          {engine === "cpu" ? "CPU-WASM running…" : "WebGPU running…"}
        </p>
      </div>
    );
  }
  return (
    <div className="w-full">
      <div className="relative h-1.5 w-full overflow-hidden bg-paper-border dark:bg-term-border">
        <div
          ref={barRef}
          className="h-full w-0 bg-emerald-700 dark:bg-emerald-500"
          role="progressbar"
          // Clamp to [0, total]: `done > total` is a supported case (the
          // visual fill clamps too), but a raw `aria-valuenow > aria-valuemax`
          // is an invalid ARIA range for assistive tech.
          aria-valuenow={Math.max(0, Math.min(done, total))}
          aria-valuemin={0}
          aria-valuemax={total}
        />
      </div>
      <p className="mt-1 text-xs font-semibold tabular-nums text-paper-bright dark:text-term-bright">
        {target.toFixed(1)}%
        {spinsPerSec != null && ` · ${formatThroughput(spinsPerSec)}`}
      </p>
    </div>
  );
}
